"use client";

import { useState } from "react";
import {
  Notice,
  type Option,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
  levelOptions,
} from "@/components/tools/stats-tests/kit";
import { ZTestResults } from "@/components/tools/stats-tests-more/z-test-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  parseIntegerInput,
  parseNumberInput,
  parseNumberList,
} from "@/lib/parse-number";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";
import {
  oneSampleZTest,
  summarizeSample,
  twoSampleZTest,
  type ZTestOutcome,
} from "@/lib/tool-kit/stats-tests-more/z-test";

/** English strings. A translated content file overrides these through `ui`. */
export const Z_TEST_CALCULATOR_STRINGS = {
  testLabel: "Test",
  oneSample: "One sample",
  twoSample: "Two independent samples",
  entryLabel: "Enter the data as",
  summaryEntry: "Summary statistics",
  rawEntry: "Raw data",
  sampleTitle: "Sample {n}",
  meanLabel: "Sample mean (x̄)",
  nLabel: "Sample size (n)",
  sigmaLabel: "Population SD (σ), known",
  dataLabel: "Values — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "104, 98, 112, 107, 101",
  valuesDetected: "one: value|other: values",
  mu0Label: "Hypothesised mean (μ₀)",
  delta0Label: "Hypothesised difference (Δ₀)",
  delta0Hint: "Use 0 to test whether the two means are equal.",
  alternativeLabel: "Alternative hypothesis",
  twoSidedOne: "Two-sided: the mean is not μ₀",
  lessOne: "The mean is less than μ₀",
  greaterOne: "The mean is greater than μ₀",
  twoSidedTwo: "Two-sided: the means differ by something other than Δ₀",
  lessTwo: "Mean 1 − mean 2 is less than Δ₀",
  greaterTwo: "Mean 1 − mean 2 is greater than Δ₀",
  alphaLabel: "Significance level (α)",
  levelLabel: "Confidence level for the interval",
  tryExample: "Try an example",
  exampleOne: "104.2",
  exampleOneN: "36",
  exampleOneSigma: "15",
  exampleOneMu0: "100",
  exampleMean1: "512",
  exampleN1: "40",
  exampleSigma1: "12",
  exampleMean2: "498",
  exampleN2: "45",
  exampleSigma2: "15",
  exampleDelta0: "0",
  exampleRaw1: "104, 98, 112, 107, 101, 95, 110, 103, 99, 108",
  exampleRaw2: "96, 102, 99, 105, 93, 100, 97, 104, 98, 101",
  results: "Results",
  zStatistic: "z statistic",
  pValue: "p-value",
  estimateOne: "Sample mean",
  estimateTwo: "Difference of means",
  standardError: "Standard error",
  ciLabel: "{level} confidence interval",
  cohenD: "Cohen's d",
  criticalLine: "Critical z at α = {alpha}: {value}.",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the data are inconsistent with the hypothesised value.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence against the hypothesised value.",
  pValuesHeading: "All three p-values",
  alternative: "Alternative",
  p: "p",
  chartHeading: "Where z falls on the standard normal curve",
  chartLabel:
    "Standard normal curve with the rejection region shaded and a line at the observed z",
  chartCaption:
    "The shaded area is the rejection region at α = {alpha}; the dashed line marks z = {z}.",
  sampleSummaryHeading: "Raw data summary",
  sampleSummary:
    "Sample {n}: n = {count}, mean = {mean}, sample SD = {sd} (shown for reference — σ is taken as known).",
  howCalculated: "How this was calculated",
  workingSeOne: "SE = σ / √n = {sigma} / √{n} = {se}",
  workingZOne: "z = (x̄ − μ₀) / SE = ({mean} − {mu0}) / {se} = {z}",
  workingSeTwo: "SE = √(σ₁²/n₁ + σ₂²/n₂) = √({s1}²/{n1} + {s2}²/{n2}) = {se}",
  workingZTwo: "z = (x̄₁ − x̄₂ − Δ₀) / SE = ({diff} − {delta0}) / {se} = {z}",
  workingP: "Two-sided p = 2 × P(Z ≥ |z|) = {p}; z* for {level} = {crit}.",
  workingCi:
    "{level} CI = estimate ± z* × SE = {est} ± {crit} × {se} = [{lo}, {hi}]",
  workingD: "Cohen's d = {num} / {den} = {d}",
  apaHeading: "APA-style result",
  apaOneTemplate:
    "*M* = {m}, *z* = {z}, *p* {p}, {level} CI [{lo}, {hi}], *d* = {d}",
  apaTwoTemplate:
    "*M*diff = {m}, *z* = {z}, *p* {p}, {level} CI [{lo}, {hi}], *d* = {d}",
  apaNote:
    "A z-test needs a population SD known from theory or from a very large earlier sample. If σ is estimated from your data, use a t-test.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  promptOne: "Enter the sample mean, n, σ, and μ₀ — or paste your raw data.",
  promptTwo:
    "Enter the mean, n, and σ of both samples — or paste both data sets.",
  errorSigma: "The population SD (σ) must be a positive number.",
  errorN: "The sample size must be a whole number of at least 1.",
} as const;

type Mode = "one" | "two";
type Entry = "summary" | "raw";

interface SampleState {
  mean: string;
  n: string;
  sigma: string;
  data: string;
}

const EMPTY_SAMPLE: SampleState = { mean: "", n: "", sigma: "", data: "" };

function SampleFields({
  id,
  title,
  entry,
  state,
  onChange,
  strings,
  count,
}: {
  id: string;
  title: string | null;
  entry: Entry;
  state: SampleState;
  onChange: (next: SampleState) => void;
  strings: typeof Z_TEST_CALCULATOR_STRINGS;
  count: string;
}) {
  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4">
      {title ? (
        <h3 className="font-medium text-foreground text-sm">{title}</h3>
      ) : null}
      {entry === "summary" ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id={`${id}-mean`}
            label={strings.meanLabel}
            numeric
            onChange={(mean) => onChange({ ...state, mean })}
            value={state.mean}
          />
          <TextField
            id={`${id}-n`}
            label={strings.nLabel}
            numeric
            onChange={(n) => onChange({ ...state, n })}
            value={state.n}
          />
          <TextField
            id={`${id}-sigma`}
            label={strings.sigmaLabel}
            numeric
            onChange={(sigma) => onChange({ ...state, sigma })}
            value={state.sigma}
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            hint={count}
            id={`${id}-data`}
            label={strings.dataLabel}
            onChange={(data) => onChange({ ...state, data })}
            placeholder={strings.dataPlaceholder}
            rows={3}
            value={state.data}
          />
          <TextField
            id={`${id}-sigma`}
            label={strings.sigmaLabel}
            numeric
            onChange={(sigma) => onChange({ ...state, sigma })}
            value={state.sigma}
          />
        </div>
      )}
    </div>
  );
}

export interface SampleNumbers {
  mean: number;
  n: number;
  sigma: number | null;
  /** The sample SD, for raw data only. */
  sd: number | null;
}

/** Mean and n of a sample entered either way, or null. */
function sampleNumbers(entry: Entry, state: SampleState): SampleNumbers | null {
  const sigma = parseNumberInput(state.sigma);
  if (entry === "raw") {
    const values = parseNumberList(state.data);
    if (values.length === 0) {
      return null;
    }
    const summary = summarizeSample(values);
    return {
      mean: summary.mean,
      n: values.length,
      sigma,
      sd: Number.isNaN(summary.sd) ? null : summary.sd,
    };
  }
  const mean = parseNumberInput(state.mean);
  const n = parseIntegerInput(state.n);
  return mean !== null && n !== null ? { mean, n, sigma, sd: null } : null;
}

export default function ZTestCalculatorWidget() {
  const strings = useToolUi(Z_TEST_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("one");
  const [entry, setEntry] = useState<Entry>("summary");
  const [first, setFirst] = useState<SampleState>(EMPTY_SAMPLE);
  const [second, setSecond] = useState<SampleState>(EMPTY_SAMPLE);
  const [nullText, setNullText] = useState("");
  const [alternative, setAlternative] = useState<Alternative>("two-sided");
  const [alpha, setAlpha] = useState("0.05");
  const [level, setLevel] = useState("0.95");

  const a = sampleNumbers(entry, first);
  const b = mode === "two" ? sampleNumbers(entry, second) : null;
  const nullValue = parseNumberInput(nullText);
  const samples = [a, b].filter((x): x is SampleNumbers => x !== null);
  const options = {
    alternative,
    alpha: Number(alpha),
    level: Number(level),
  };
  let outcome: ZTestOutcome | null = null;
  if (mode === "one" && a && a.sigma !== null && nullValue !== null) {
    outcome = oneSampleZTest(
      { mean: a.mean, n: a.n, sigma: a.sigma, mu0: nullValue },
      options
    );
  } else if (
    mode === "two" &&
    a &&
    b &&
    a.sigma !== null &&
    b.sigma !== null &&
    nullValue !== null
  ) {
    outcome = twoSampleZTest(
      {
        mean1: a.mean,
        n1: a.n,
        sigma1: a.sigma,
        mean2: b.mean,
        n2: b.n,
        sigma2: b.sigma,
        delta0: nullValue,
      },
      options
    );
  }
  const labels =
    mode === "one"
      ? [strings.twoSidedOne, strings.lessOne, strings.greaterOne]
      : [strings.twoSidedTwo, strings.lessTwo, strings.greaterTwo];
  const alternatives: Option<Alternative>[] = [
    { value: "two-sided", label: labels[0] },
    { value: "less", label: labels[1] },
    { value: "greater", label: labels[2] },
  ];
  const countOf = (state: SampleState) => {
    const n = parseNumberList(state.data).length;
    return `${fmt(n)}${tPlural(strings.valuesDetected, locale, n)}`;
  };

  let notice: string = mode === "one" ? strings.promptOne : strings.promptTwo;
  if (outcome && !outcome.ok) {
    notice =
      outcome.reason === "invalid-sigma" ? strings.errorSigma : strings.errorN;
  }

  function fillExample() {
    if (entry === "raw") {
      setFirst({
        ...EMPTY_SAMPLE,
        data: strings.exampleRaw1,
        sigma: strings.exampleSigma1,
      });
      setSecond({
        ...EMPTY_SAMPLE,
        data: strings.exampleRaw2,
        sigma: strings.exampleSigma2,
      });
      setNullText(
        mode === "one" ? strings.exampleOneMu0 : strings.exampleDelta0
      );
      return;
    }
    if (mode === "one") {
      setFirst({
        mean: strings.exampleOne,
        n: strings.exampleOneN,
        sigma: strings.exampleOneSigma,
        data: "",
      });
      setNullText(strings.exampleOneMu0);
      return;
    }
    setFirst({
      mean: strings.exampleMean1,
      n: strings.exampleN1,
      sigma: strings.exampleSigma1,
      data: "",
    });
    setSecond({
      mean: strings.exampleMean2,
      n: strings.exampleN2,
      sigma: strings.exampleSigma2,
      data: "",
    });
    setNullText(strings.exampleDelta0);
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.testLabel}
          name="zt-mode"
          onChange={setMode}
          options={[
            { value: "one", label: strings.oneSample },
            { value: "two", label: strings.twoSample },
          ]}
          value={mode}
        />
        <PillGroup
          label={strings.entryLabel}
          name="zt-entry"
          onChange={setEntry}
          options={[
            { value: "summary", label: strings.summaryEntry },
            { value: "raw", label: strings.rawEntry },
          ]}
          value={entry}
        />
      </div>
      <SampleFields
        count={countOf(first)}
        entry={entry}
        id="zt-a"
        onChange={setFirst}
        state={first}
        strings={strings}
        title={mode === "two" ? t(strings.sampleTitle, { n: 1 }) : null}
      />
      {mode === "two" ? (
        <SampleFields
          count={countOf(second)}
          entry={entry}
          id="zt-b"
          onChange={setSecond}
          state={second}
          strings={strings}
          title={t(strings.sampleTitle, { n: 2 })}
        />
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={mode === "two" ? strings.delta0Hint : undefined}
          id="zt-null"
          label={mode === "one" ? strings.mu0Label : strings.delta0Label}
          numeric
          onChange={setNullText}
          value={nullText}
        />
        <SelectField
          id="zt-alternative"
          label={strings.alternativeLabel}
          onChange={setAlternative}
          options={alternatives}
          value={alternative}
        />
        <SelectField
          id="zt-alpha"
          label={strings.alphaLabel}
          onChange={setAlpha}
          options={alphaOptions(fmt)}
          value={alpha}
        />
        <SelectField
          id="zt-level"
          label={strings.levelLabel}
          onChange={setLevel}
          options={levelOptions(fmt)}
          value={level}
        />
      </div>
      <ExampleButton label={strings.tryExample} onClick={fillExample} />
      {outcome?.ok ? (
        <ZTestResults
          alpha={Number(alpha)}
          fmt={fmt}
          r={outcome.result}
          samples={samples}
          strings={strings}
        />
      ) : (
        <Notice>{notice}</Notice>
      )}
    </div>
  );
}
